// A control graphic as a card in the plan rail. The glyph is the shape the graphic takes on the map —
// an arrow for an axis of advance, a closed polygon for an objective, a line for a phase line, a dashed
// circle for a named area of interest — so the card and the overlay read as the same object (spec §8).

import { useEffect, useRef, useState } from 'react';
import type { Graphic } from '../../sim/types';
import { toolMeta, type DrawKind } from '../../map/draw';

export function ShapeGlyph({ kind, size = 22, color }: { kind: DrawKind; size?: number; color?: string }) {
  const stroke = color ?? (kind === 'nai' ? 'var(--color-isr)' : 'var(--color-friendly)');
  const common = { fill: 'none', stroke, strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
      {kind === 'axis' && (
        <>
          <path d="M3 18 L10 12 L17 8" {...common} />
          <path d="M13 6 L19 7 L17 13 Z" {...common} fill={stroke} />
        </>
      )}
      {kind === 'obj' && <path d="M5 8 L13 4 L20 10 L16 19 L6 17 Z" {...common} />}
      {kind === 'pl' && (
        <>
          <path d="M3 12 H21" {...common} />
          <path d="M6 9 V15 M18 9 V15" {...common} />
        </>
      )}
      {kind === 'nai' && <circle cx="12" cy="12" r="7.5" {...common} strokeDasharray="3 2.5" />}
    </svg>
  );
}

export function GraphicCard({
  graphic, drawn, onRename, onRemove, highlight, onHover,
}: {
  graphic: Graphic;
  /** Fitted graphics come with the scenario and are not the player's to delete. */
  drawn: boolean;
  onRename: (name: string) => void;
  onRemove: () => void;
  highlight?: boolean;
  onHover?: (over: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(graphic.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(graphic.name); }, [graphic.name]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const kind = graphic.kind === 'aa' ? 'obj' : (graphic.kind as DrawKind);
  const commit = (): void => { setEditing(false); onRename(draft); };

  return (
    <div
      data-testid={`graphic-${graphic.id}`}
      data-graphic-id={graphic.id}
      onDragOver={(e) => { e.preventDefault(); onHover?.(true); }}
      onDragLeave={() => onHover?.(false)}
      className="wt-card flex items-center gap-2"
      style={{ borderColor: highlight ? 'var(--color-friendly)' : 'var(--color-slate-700)' }}
      title={`${toolMeta(kind).expansion}. Drop a unit card here to task it against this graphic.`}
    >
      <ShapeGlyph kind={kind} />
      <div className="min-w-0 flex-1">
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') { setDraft(graphic.name); setEditing(false); }
            }}
            data-testid={`graphic-rename-${graphic.id}`}
            className="mono w-full rounded-sm border border-slate-700 bg-ink px-1 py-0.5 text-[11px] uppercase tracking-[0.1em] text-paper outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            title="Click to rename"
            className="mono block w-full truncate text-left text-[11px] uppercase tracking-[0.1em] text-paper"
          >
            {graphic.name}
          </button>
        )}
        <span className="mono block text-[9px] uppercase tracking-[0.15em] text-fog">
          {drawn ? 'drawn' : 'from the order'} · {graphic.pts.length} pt{graphic.pts.length === 1 ? '' : 's'}
        </span>
      </div>
      {drawn && (
        <button
          type="button"
          onClick={onRemove}
          data-testid={`graphic-remove-${graphic.id}`}
          title="Remove this graphic"
          className="mono shrink-0 rounded-sm border border-slate-700 px-1.5 py-0.5 text-[10px] text-fog hover:border-[var(--color-hostile)] hover:text-[var(--color-hostile)]"
        >
          ✕
        </button>
      )}
    </div>
  );
}
